import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { CheckType } from '@app/core/model/entities/asset/check-type';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import { CheckTypeService } from '@app/shared/services/check-type.service';

@Component({
  selector: 'list-field-builder',
  templateUrl: './list-check-result-field-builder.component.html',
  styleUrls: ['./list-check-result-field-builder.component.scss'],
  standalone: false
})
export class ListCheckResultFieldBuilderComponent extends AbstractFieldBuilder<FormControl> implements OnInit, AfterViewInit {
  private readonly checkTypeService = inject(CheckTypeService);

  public Permission = PermissionEnum;
  public checkTypes: { [k: string]: CheckType };
  // Kept as a single instance: the NOT_IN_ARRAY validator reads it on every validation
  public readonly allowedValues: string[] = [];

  constructor() {
    super();
    this.checkTypeService.checkTypes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(checkTypes => {
      this.checkTypes = checkTypes;
      this.setAllowedValues();
    });
    // On the offchance the observation was never set beforehand
    this.fieldInitValue = this.fieldInitValue || '';
  }

  public ngOnInit(): void {
    this.form = new FormGroup({
      field: new FormControl(
        this.fieldInitValue,
        this.computeValidators()
      )
    });
    // Initialise the field in the registry
    this.setFieldValue(this.fieldInitValue);
    this.setFieldInitialValue(this.fieldInitValue);

    this.getNextState();
  }

  public ngAfterViewInit(): void {
    this.setupHooks();

    this.form.controls.field.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.setFieldValue(value);
    });
  }

  public cancel(): void {
    this.form.controls.field.setValue(this.getFieldInitialValue());
    super.cancel();
  }

  protected computeValidators(): ValidatorFn {
    this.fieldConfig.field?.validators.push(<FieldValidator>{
      type: ValidatorType.NOT_IN_ARRAY,
      definition: this.allowedValues
    });
    return super.computeValidators();
  }

  protected setupHooks(): void {
    this.getFieldGroupInfo(['isDisplayed']).subscribe(() => {
      this.form.controls.field.setValue(this.getFieldValue());
      this.getNextState();
    });
    super.setupHooks();
  }

  /**
   * Refresh the values the Check's CheckType allows for this result Field, in place so that the validator sees them.
   * @return void
   */
  private setAllowedValues(): void {
    const values = (this.checkTypes[this.entity['type']]?.resultFields
      ?.find(resultField => resultField.fieldCode === this.fieldConfig.fieldCode)?.values ?? [])
      .map(resultValue => resultValue.value);
    this.allowedValues.splice(0, this.allowedValues.length, ...values);
    this.form?.controls.field.updateValueAndValidity();
  }
}
